import { formatCurrency } from '@/components/home/format';
import { MAX_ORGANIZED_MATCHES_PER_BATCH } from '@/schemas/organizedMatchSchema';
import type {
  OrganizedMatch,
  OrganizedMatchEditPayload,
  OrganizedMatchPayload,
  OrganizedMatchType,
} from '@/types/organized-match.model';
import type { Sport } from '@/types/sports.model';
import { apiErrorMessage } from '@/utils/api-error-message';
import { timeToMinutes } from '@/utils/time';
import { filled } from './turnos-model';

export type OrganizedMatchFormValues = {
  sportUid: string;
  playersPerTeam: number;
  matchType: OrganizedMatchType;
  gender: string;
  notes: string;
  players: { email: string; name: string }[];
  quantity: number;
};

export function buildCreatePayload(
  values: OrganizedMatchFormValues,
  clubId: string,
  startDate: string,
  endDate: string,
): OrganizedMatchPayload {
  const emails = values.players.map((p) => ({ email: p.email }));
  const payload: OrganizedMatchPayload = {
    club: { id: clubId },
    sport: { uid: values.sportUid },
    startDate,
    endDate,
    playersPerTeam: values.playersPerTeam,
    matchType: values.matchType,
    gender: values.gender,
    team1: { players: emails.slice(0, values.playersPerTeam) },
    team2: { players: emails.slice(values.playersPerTeam, values.playersPerTeam * 2) },
  };
  if (values.notes) payload.notes = values.notes;
  return payload;
}

export const hasPlayers = (match: OrganizedMatch): boolean => filled(match) > 0;

/** One match per free court of the sport in the slot, never more than a batch allows. */
export const maxQuantity = (
  freeCourts: Record<string, number> | undefined,
  sportUid: string,
): number =>
  Math.min(MAX_ORGANIZED_MATCHES_PER_BATCH, freeCourts?.[sportUid] ?? MAX_ORGANIZED_MATCHES_PER_BATCH);

export const freeCourtsHint = (free: number, sportName: string): string =>
  free === 1
    ? `Queda 1 cancha libre de ${sportName} en este horario.`
    : `Quedan ${free} canchas libres de ${sportName} en este horario.`;

/** With players preloaded a single match is created, whatever the quantity field says. */
export const effectiveQuantity = (values: OrganizedMatchFormValues): number =>
  values.players.length > 0 ? 1 : values.quantity;

export function buildEditPayload(
  original: OrganizedMatch,
  values: OrganizedMatchFormValues,
  startDate: string,
  endDate: string,
): OrganizedMatchEditPayload {
  const payload: OrganizedMatchEditPayload = {};
  if (startDate !== original.startDate) payload.startDate = startDate;
  if (endDate !== original.endDate) payload.endDate = endDate;
  if (values.notes !== (original.notes ?? '')) payload.notes = values.notes;

  // With players the backend only accepts schedule and notes changes.
  if (!hasPlayers(original)) {
    if (values.sportUid !== original.sport.uid) payload.sport = { uid: values.sportUid };
    if (values.playersPerTeam !== original.playersPerTeam) {
      payload.playersPerTeam = values.playersPerTeam;
    }
    if (values.matchType !== original.matchType) payload.matchType = values.matchType;
    if (values.gender !== original.gender) payload.gender = values.gender;
  }
  return payload;
}

export function summarizeBatch(results: PromiseSettledResult<unknown>[]): {
  created: number;
  failed: number;
  firstError?: string;
} {
  const rejected = results.filter(
    (r): r is PromiseRejectedResult => r.status === 'rejected',
  );
  const firstError = rejected
    .map((r) => apiErrorMessage(r.reason))
    .find(Boolean);
  return {
    created: results.length - rejected.length,
    failed: rejected.length,
    firstError,
  };
}

export function organizedMatchFeeNotice(fee: number | null | undefined): string {
  if (fee == null) {
    return 'Cuando el partido esté completo se carga el cargo de GetMatch a tu cuenta corriente. Si alguien se baja, se reintegra.';
  }
  if (fee <= 0) {
    return 'Gratis: completar el partido no genera ningún cargo en tu cuenta corriente.';
  }
  return `Cuando el partido esté completo se cargan ${formatCurrency(fee)} a tu cuenta corriente. Si alguien se baja, se reintegran.`;
}

export const COMPETITIVE_HELP =
  'La categoría la fija el primero que entra. Después pueden sumarse los de ±1.';

export const isFootball = (sport: Sport | undefined): boolean =>
  sport?.uid === 'FOOTBALL';

/** Padel, tennis and squash play singles or doubles; football 5, 7, 9 or 11 a side. */
export const playersPerTeamOptions = (sport: Sport | undefined): number[] =>
  isFootball(sport) ? [5, 7, 9, 11] : [1, 2];

export interface ScheduleValues {
  date: string;
  time: string;
  durationMinutes: number;
}

/** Splits club-local "yyyy-MM-ddTHH:mm:ss" strings by cutting text, never through Date. */
export function scheduleOf(match: OrganizedMatch): ScheduleValues {
  const diff = timeToMinutes(match.endDate.slice(11, 16)) - timeToMinutes(match.startDate.slice(11, 16));
  return {
    date: match.startDate.slice(0, 10),
    time: match.startDate.slice(11, 16),
    durationMinutes: diff > 0 ? diff : diff + 1440,
  };
}

/** Doubles when the sport allows it (the app and the mockup default to it). */
export const defaultPlayersPerTeam = (sport: Sport | undefined): number => {
  const options = playersPerTeamOptions(sport);
  return options.includes(2) ? 2 : options[0];
};
